/* ResultItem Component
 * --------------------
 * Show the result box of a tariff
 * Created on Wed Aug 30 2017
 *
 * Copyright (c) 2017 Ubiwhere
 *
 * Authors:
 * - Hugo Fonseca <hfonseca@ubiwhere.com>
 */

import React, { Component } from 'react'
import styled from 'styled-components'

import { Row } from 'react-flexbox-grid'
import { SimpleButton } from '@ubiwherenpm/adene-ui-components'

import ResultHeader from 'components/ResultHeader'

import KnowMoreIcon from './images/ic-saber-mais.svg'

import { generateMediaUrl, unitCurrency, pricesCurrency } from 'utils'

import AditionalServices from 'components/AditionalServices'
import { backgroundColors } from 'config'

import uniq from 'lodash/uniq'
import isEmpty from 'lodash/isEmpty'
import merge from 'lodash/merge'
import PrimaryButton from '../PrimaryButton'

export const GasItem = ({ prices, className }) => {
  return (
    <GasRow>
      <GasEntityCol className={className}>
        <ResultLabel ga>Gás Natural</ResultLabel>
        <Numberyx>
          <NumberyLabel>Termo Fixo</NumberyLabel>
          <Price>
            {pricesCurrency(prices.gas.termo_fixo) || '---'} <Unit>€/dia</Unit>
          </Price>
        </Numberyx>
        <Numberyx style={{ marginTop: 10 }}>
          <NumberyLabel>Energia</NumberyLabel>
          <Price>
            {pricesCurrency(prices.gas.termo_energia) || '---'}{' '}
            <Unit>€/kWh</Unit>
          </Price>
        </Numberyx>
      </GasEntityCol>
    </GasRow>
  )
}

export const EletricityItem = ({ prices, className, priceListTimeCycle }) => {
  return (
    <EletricityRow>
      <EletricityEntityCol className={className}>
        <ResultLabel el>Eletricidade</ResultLabel>
        <Numberyx>
          <NumberyLabel>Termo Potência</NumberyLabel>
          <Price>
            {pricesCurrency(prices.electricity.termo_fixo) || '---'}{' '}
            <Unit>€/dia</Unit>
          </Price>
        </Numberyx>
        <Numberyx style={{ marginTop: 10 }}>
          <NumberyLabel>Energia</NumberyLabel>
          {priceListTimeCycle === 's' && (
            <Price>
              {`${pricesCurrency(prices.electricity.cheia) || '---'}`}{' '}
              <Unit>€/kWh</Unit>
            </Price>
          )}
        </Numberyx>
        {priceListTimeCycle === 'b' && (
          <NumberyxSmall>
            <NumberyLabelSmall>Fora do Vazio</NumberyLabelSmall>{' '}
            <Price>
              {pricesCurrency(prices.electricity.cheia) || '---'}{' '}
              <Unit>€/kWh</Unit>
            </Price>
          </NumberyxSmall>
        )}
        {priceListTimeCycle !== 's' && (
          <NumberyxSmall>
            <NumberyLabelSmall>Vazio</NumberyLabelSmall>{' '}
            <Price>
              {pricesCurrency(prices.electricity.vazio) || '---'}{' '}
              <Unit>€/kWh</Unit>
            </Price>
          </NumberyxSmall>
        )}
        {priceListTimeCycle !== 'b' && priceListTimeCycle !== 's' && (
          <NumberyxSmall>
            <NumberyLabelSmall>Ponta</NumberyLabelSmall>{' '}
            <Price>
              {pricesCurrency(prices.electricity.ponta) || '---'}{' '}
              <Unit>€/kWh</Unit>
            </Price>
          </NumberyxSmall>
        )}
        {priceListTimeCycle !== 'b' && priceListTimeCycle !== 's' && (
          <NumberyxSmall>
            <NumberyLabelSmall>Cheias</NumberyLabelSmall>{' '}
            <Price>
              {pricesCurrency(prices.electricity.cheia) || '---'}{' '}
              <Unit>€/kWh</Unit>
            </Price>
          </NumberyxSmall>
        )}
      </EletricityEntityCol>
    </EletricityRow>
  )
}

export default class ResultItem extends Component {
  render() {
    const {
      annualBilling,
      bestAnnualBilling,
      clickMore,
      clickAdhere,
      translations,
    } = this.props

    const { ADHERE_BUTTON } = translations

    let entity
    let designation
    let subscriptions
    let medalText
    let medalType

    // Get values depending if electricity or gas
    if (this.props.electricityPriceList) {
      entity = this.props.electricityPriceList.entity
      designation = this.props.electricityPriceList.designation
      subscriptions = this.props.electricityPriceList.subscriptions
    }

    if (this.props.gasPriceList) {
      entity = this.props.gasPriceList.entity
      designation = this.props.gasPriceList.designation
      subscriptions = this.props.gasPriceList.subscriptions
    }

    /** 
     * 
     * Ouro – mais de 50 adesões (inclusive)
      Prata – mais de 20 adesões (inclusive)
      Bronze – mais de 5 adesões (inclusive)
      "nenhuma medalha" – <5 adesões.
     */

    if (subscriptions.count >= 50) {
      medalText = `+50 ${subscriptions.period}`
      medalType = 'gold'
    } else if (subscriptions.count >= 20) {
      medalText = `+20 ${subscriptions.period}`
      medalType = 'silver'
    } else if (subscriptions.count >= 5) {
      medalText = `+5 ${subscriptions.period}`
      medalType = 'bronze'
    }

    // Calculate money saved
    const moneySaved = bestAnnualBilling - annualBilling

    // Set Logo absolute url
    entity.absoluteLogoUrl =
      entity.hi_res_logo && generateMediaUrl(entity.hi_res_logo)

    // Merge Additional Services
    let additionalServices = []
    if (this.props.electricityPriceList) {
      additionalServices = merge(additionalServices, [
        ...this.props.electricityPriceList.additional_services,
      ])
    }

    if (this.props.gasPriceList) {
      additionalServices = merge(additionalServices, [
        ...this.props.gasPriceList.additional_services,
      ])
    }

    additionalServices = uniq(additionalServices)

    // Info needed for KnowMore Modal
    const moreInfoParams = { ...this.props, moneySaved }

    moreInfoParams.priceListId = this.props.id

    if (this.props.electricityPriceList) {
      moreInfoParams.id = this.props.electricityPriceList.id
    } else {
      if (this.props.gasPriceList) {
        moreInfoParams.id = this.props.gasPriceList.id
      }
    }

    return (
      <Wrapper>
        <ResultHeader
          designation={designation}
          timeCycle={this.props.priceListTimeCycle}
          absoluteLogoUrl={entity.absoluteLogoUrl}
          medalText={medalText}
          medalType={medalType}
          shareUrl={this.props.url}
        />
        <Box>
          <FaturaCol>
            <WhiteNumber
              isNegative={moneySaved <= 0}
              bgColor={backgroundColors.normalLeft}>
              <TieLabel>{moneySaved <= 0 ? `Gasta` : `Poupa`}</TieLabel>
              <br />{' '}
              {`${moneySaved <= 0 ? `+` : ``}${moneySaved &&
                Math.abs(Math.round(moneySaved))}`}{' '}
              €/ano
            </WhiteNumber>
            <TitleLabel>Fatura de Energia *</TitleLabel>
            <NumberyAnnual>
              {annualBilling && unitCurrency(annualBilling)} €/ano
            </NumberyAnnual>
            <SmallNumber>
              {annualBilling && unitCurrency(annualBilling / 12)} €/mês
            </SmallNumber>
          </FaturaCol>
          <Scrollable>
            {this.props.electricityPriceList && (
              <EletricityItem
                {...this.props}
                entity={false}
                electricityConsumption={false}
              />
            )}

            {this.props.gasPriceList && (
              <GasItem {...this.props} entity={false} gasConsumption={false} />
            )}

            {additionalServices && !isEmpty(additionalServices) && (
              <AdditionalWrapper>
                <AditionalServices list={additionalServices} />
              </AdditionalWrapper>
            )}
          </Scrollable>
          <ButtonsList>
            <PrimaryButton
              style={{ marginBottom: 20 }}
              text={ADHERE_BUTTON}
              onClick={() => clickAdhere(moreInfoParams)}></PrimaryButton>
            <Button
              onClick={() => clickMore(moreInfoParams)}
              icon={KnowMoreIcon}>
              Saber Mais
            </Button>
          </ButtonsList>
        </Box>
      </Wrapper>
    )
  }
}

/* eslint-enable camelcase */

const Wrapper = styled(Row)`
  margin: 0 !important;
`

const Box = styled(Row)`
  background: #fff;
  min-height: 162px;
  border: 1px solid #efefef;
  margin: 0;
  margin-bottom: 20px;
  width: 100%;
  position: relative;
  margin-right: 0 !important;
  margin-left: 0 !important;

  @media (max-width: 980px) {
    display: flex !important;
    flex-direction: column !important;
  }
`

const Button = styled(SimpleButton)`
  width: 100%;
  flex: 1;
  border-radius: 0px;
  &.selected {
  }
`

const ButtonsList = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media (max-width: 980px) {
    position: initial;
    max-width: none;
    flex: 1;
    flex-direction: row;
  }
`

export const FaturaCol = styled.div`
  padding: 0 20px;
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: inset -1px 0 0 0 #efefef;
  max-width: 150px;
  background-color: #676767;

  @media (max-width: 980px) {
    max-width: none;
  }
`

const EletricityRow = styled.div`
  display: flex;
  flex-direction: row;
`

const GasRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0px 20px 0px 27px;
`

const Numberyx = styled.span`
  color: #676767;
  font-size: 14px;
  font-weight: bold;
  line-height: 18px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin: 2px 0;
`

const Unit = styled.span`
  font-weight: normal;
`

const Price = styled.span``

const NumberyLabel = styled.span`
  color: #676767;
  font-size: 14px;
  font-weight: 100;

  margin-right: 10px;
  display: inline-block;
  vertical-align: middle;
`

const NumberyxSmall = styled.span`
  font-size: 12px;
  line-height: 12px;
  color: #676767;
  font-weight: bold;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin: 2px 0;
`

const TieLabel = styled.span`
  margin-right: 23px;
`

const NumberyLabelSmall = styled.span`
  color: #676767;
  font-size: 12px;
  font-weight: 100;

  margin-right: 10px;
  display: inline-block;
  vertical-align: middle;
`

const GasEntityCol = styled.div`
  flex: 0.25;
  text-align: left;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-right: 30px;
  justify-content: flex-start;
  min-width: 165px;
  margin-top: 5%;

  @media (max-width: 980px) {
    min-width: none;
    min-height: 120px;
  }
`

const EletricityEntityCol = styled.div`
  flex: 0.25;
  text-align: left;
  padding: 0 30px;
  margin-right: 30px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 250px;
  margin-top: 5%;

  @media (max-width: 980px) {
    min-width: none;
    min-height: 120px;
  }
`

const ResultLabel = styled.span`
  display: block;
  font-size: 14px;
  font-weight: bold;
  line-height: 14px;
  margin: 10px 0px 12px 0px;
  color: ${({ el }) => (el ? `#FF8E2A` : `#00BDED`)};

  span {
    color: #ccc;
  }
`

export const SavedCol = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  box-shadow: inset -1px 0 0 0 #efefef;
  background: ${({ bgColor }) => bgColor};
  padding: 0 20px;
  max-width: 150px;

  ${({ isNegative }) =>
    isNegative &&
    `
  background: linear-gradient(270deg,#3E3E3E,#9C9C9C);
  `}

  @media(max-width: 980px) {
    min-width: 115px;
  }
`

export const Numbery = styled.span`
  color: white;
  font-size: 18px;
  line-height: 18px;
  font-weight: bold;
  margin: 10px 0;
  margin-top: 0;
`

const SmallNumber = styled(Numbery)`
  font-size: 13px;
`

const WhiteNumber = styled(Numbery)`
  position: relative;
  width: 125%;
  font-size: 20px;
  font-weight: bold;
  padding: 5px 0 5px 10px;
  line-height: 22px;
  margin: -10px 5px 1px -62px;
  color: white;
  background: ${({ bgColor }) => bgColor};

  ${({ isNegative }) =>
    isNegative &&
    `
    background: linear-gradient(90deg, #C13E44 0%, #920F15 100%);
  `}

  ::after {
    content: ' ';
    position: absolute;
    width: 0;
    height: 0;
    left: 0px;
    top: 100%;
    border-width: 5px 16.5px;
    border-style: solid;
    border-color: #7f7f7f #7f7f7f transparent transparent;
  }

  ::before {
    content: ' ';
    position: absolute;
    width: 0;
    height: 0;
    right: -2px;
    top: 0px;
    border-color: transparent #fff transparent transparent;
  }

  @media (max-width: 1280px) {
    width: 100%;
    margin: -10px 22px 1px -62px;

    ::after {
      border-width: 5px 13.5px;
    }
  }
`

const NumberyAnnual = styled(Numbery)`
  font-size: 16px;
  margin: 0px !important;
`

const TitleLabel = styled.span`
  display: block;
  font-size: 13px;
  font-weight: bold;
  line-height: 14px;
  margin: 12px 0;
  color: #ccc;
  white-space: nowrap;
`

const Scrollable = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 980px) {
    margin-bottom: 5%;
  }

  @media (max-width: 850px) {
    flex-direction: column;
  }
`

const AdditionalWrapper = styled.div`
  display: flex;
  margin-left: 30px;

  @media (max-width: 980px) {
    min-width: 195px;
    min-height: 120px;
  }

  @media (max-width: 850px) {
    margin-top: -5% !important;
  }
`



// WEBPACK FOOTER //
// ./src/components/ResultItem/index.js